import { Button, Input, Select, Table } from 'antd';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import { refinanceStateReset } from '../../../features/refinance/refinanceSlice';
import { numberWithCommas } from '../../../helpers/number';
import { getApprovedReviewRefinancesThunk } from '../../../services/refinanceService/refinanceThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDate, displayDateAndTime } from '../../../utils/datetime.util';

import type { Refinance } from '../../../features/refinance/refinanceSlice';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { getAdminsThunk } from '../../../services/adminService/adminThunk';

const { Search } = Input;

const RefinanceApplicationApprovedReviewPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { approvedReviewRefinanceList } = useSelector((state: RootState) => state.refinance);
  const { admins } = useSelector((state: RootState) => state.admin);

  const [searchFiled, setSearchFiled] = useState('applicationNo');
  const [searchValue, setSearchValue] = useState('');
  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'submissionDate',
      search: {},
      filters: {},
    }),
    []
  );

  const [sortedInfo, setSortedInfo] = useState<SorterResult<Refinance>>({});
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);

  const tablePagination = {
    total: approvedReviewRefinanceList?.data?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Application Date & Time',
      dataIndex: ['application', 'submissionDate'],
      key: 'submissionDate',
      sorter: (a, b) => a.application.submissionDate - b.application.submissionDate,
      sortOrder: sortedInfo.columnKey === 'submissionDate' ? sortedInfo.order : null,
      render: (text) => (text ? displayDateAndTime(text) : '-'),
    },
    {
      title: 'Approval Date & Time',

      render: (text, record: any) => {
        return record?.application?.agreement?.AgreementStepStatus[0]
          ? displayDateAndTime(record?.application?.agreement?.AgreementStepStatus[0]?.updatedAt)
          : '-';
      },
    },
    {
      title: 'Application No.',
      dataIndex: ['application', 'applicationNo'],
      key: 'applicationNo',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/application/details/${record.application.id}`)}
        >
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Applicant Name',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) => record?.application?.applicantFirstName ?? '-',
    },
    {
      title: 'Applicant NRIC',
      render: (_, record) => record?.application?.applicantNric ?? '-',
    },
    {
      title: 'Existing Outstanding Balance',
      dataIndex: ['totalLoanOutstanding'],
      key: 'totalLoanOutstanding',
      render: (text) => (text ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Refinance Amount',
      dataIndex: ['application', 'financeAmount'],
      key: 'financeAmount',
      render: (text) => (text != null ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Effective Date',
      dataIndex: ['effectiveDate'],
      key: 'effectiveDate',
      render: (text) => (text ? displayDate(text) : '-'),
    },
  ];

  const fetchRefinances = async (params?: any) => {
    await dispatch(getApprovedReviewRefinancesThunk(params));
  };

  const fetchAdmins = async (params?: any) => {
    await dispatch(getAdminsThunk({ params }));
  };

  const resetRefinanceState = async () => {
    await dispatch(refinanceStateReset());
  };

  const onSearch = (value: string) => {
    value = value.trim();
    setSearchValue(value);
    if (value) {
      fetchRefinances({
        ...initialParams,
        filters: { ...filterState },
        search: {
          [searchFiled]: value,
        },
      });
    } else {
      fetchRefinances({
        ...initialParams,
        filters: { ...filterState },
      });
    }

    setFilterState({ ...initialParams.filters });
  };

  const handleTableChange: TableProps<Refinance>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    const param =
      searchValue.length === 0
        ? {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey ?? initialParams.sortField,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
          }
        : {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey ?? initialParams.sortField,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
            search: {
              [searchFiled]: searchValue,
            },
          };

    fetchRefinances(param);
    setSortedInfo(sorter as SorterResult<Refinance>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useLifecycles(
    () => {
      resetRefinanceState();
      fetchRefinances(initialParams);
      fetchAdmins({
        pagination: {
          current: 1,
          pageSize: 10000, // get all admins
        },
      });
    },
    () => {
      resetRefinanceState();
    }
  );

  return (
    <>
      <Helmet>
        <title>Refinance Approved Review - redCASH CEP</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Refinance Approved Review " />
      {/* <Radio.Group defaultValue="a" buttonStyle="solid" optionType="button">
        <Radio.Button value="a">All</Radio.Button>
        <Radio.Button value="b">Credit Assessment (Approval)</Radio.Button>
        <Radio.Button value="c">Credit Assessment (KIV Approval)</Radio.Button>
        <Radio.Button value="d">System Rejected</Radio.Button>
      </Radio.Group> */}
      <Input.Group compact>
        <Select
          defaultValue="applicationNo"
          style={{ width: 200 }}
          onChange={(value) => setSearchFiled(value)}
          options={[
            { value: 'applicationNo', label: 'Application No.' },
            { value: 'applicantFirstName', label: 'Applicant Name' },
            { value: 'applicantNric', label: 'Applicant NRIC' },
          ]}
        />
        <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>
      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={approvedReviewRefinanceList?.data?.data ?? []}
        loading={approvedReviewRefinanceList?.isLoading}
        onChange={handleTableChange}
      />
    </>
  );
};

export default RefinanceApplicationApprovedReviewPage;
